import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { TooltipProvider } from '@/components/ui/tooltip';
import '@/events/mailEventHandlers';
import { initTheme } from '@/stores/themeStore';
import { initTapDebug } from '@/debug/tapDebug';
import { guardVisualViewportHeight } from '@/lib/visualViewportFix';

// Before anything positions a popper: Safari can report visualViewport.height 0.
guardVisualViewportHeight();

// Paint the saved theme onto <html> before React renders, so the first frame
// is already in the right palette rather than flashing the CSS defaults.
initTheme();

// Off unless ?tapdebug=1 or a three-finger hold switches it on (see the file).
initTapDebug();

// Redirect path-based URLs to hash-based URLs for PWA compatibility
// This runs before React renders to ensure proper initial state
(function redirectPathToHash() {
  const { pathname, search, hash } = window.location;

  // If there's already a hash route, nothing to do
  if (hash && hash.startsWith('#/')) {
    return;
  }

  // Convert any path AND/OR query string into a hash route, preserving the
  // search params. Legacy notification deep links arrive as
  // `/?account=1&folder=2&email=3`; the route parser still understands those
  // params, but only if we keep them when moving them into the hash. Dropping
  // `search` here was silently discarding the deep link.
  if (pathname !== '/' || search) {
    const hashPath = '#' + pathname + search;
    window.history.replaceState(null, '', '/' + hashPath);
    return;
  }

  // Plain root with no path/search/hash -> default hash route
  window.history.replaceState(null, '', '/#/');
})();

// Suppress harmless source map warnings from browser extensions (React DevTools, etc.)
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const message = args[0];
    if (
      typeof message === 'string' &&
      (message.includes('Source map error') ||
        message.includes('installHook.js.map') ||
        message.includes('react_devtools_backend_compact.js.map') ||
        message.includes('can\'t access property "sources"'))
    ) {
      return;
    }
    originalWarn.apply(console, args);
  };
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <TooltipProvider>
      <App />
    </TooltipProvider>
  </StrictMode>
);
